import { EventEmitter } from 'events';
import { buildMouseDownInteraction, buildMouseMoveInteraction, buildMouseUpInteraction, buildMouseWheelInteraction } from './user-interaction-builder.js';
// https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
export var MouseButton;
(function (MouseButton) {
    MouseButton[MouseButton["Left"] = 0] = "Left";
})(MouseButton || (MouseButton = {}));
const inputConfig = {
    pinchZoomThresholdInPixel: 50
};
export default class InputHandler extends EventEmitter {
    logger;
    dataChannel;
    videoElement;
    previousPosition = { clientX: 0, clientY: 0 };
    previousPinchDistance = 0;
    handlers = [];
    constructor(logger, dataChannel, videoElement) {
        super();
        this.logger = logger;
        this.dataChannel = dataChannel;
        this.videoElement = videoElement;
    }
    static connect(props) {
        const handler = new InputHandler(props.logger, props.dataChannel, props.videoElement);
        handler.registerMouseEvents();
        handler.registerTouchEvents();
        return handler;
    }
    disconnect() {
        this.handlers.forEach(([event, handler]) => {
            this.videoElement.removeEventListener(event, handler);
        });
    }
    bind(eventType, handler) {
        this.handlers.push([eventType, handler]);
        this.videoElement.addEventListener(eventType, (event) => {
            handler(event);
            event.preventDefault();
            this.emit('userInteraction');
        }, { passive: false } // otherwise this defaults to true for some events and causes console warnings as we always preventDefault
        );
    }
    registerMouseEvents() {
        this.logger.debug('Registering mouse input events');
        this.bind('mousemove', (mouseEvent) => {
            this.dataChannel.send(buildMouseMoveInteraction(this.videoElement, mouseEvent));
        });
        this.bind('wheel', (wheelEvent) => {
            this.dataChannel.send(buildMouseWheelInteraction(this.videoElement, wheelEvent));
        });
        this.bind('mousedown', (mouseEvent) => {
            this.dataChannel.send(buildMouseDownInteraction(this.videoElement, mouseEvent));
        });
        this.bind('mouseup', (mouseEvent) => {
            this.dataChannel.send(buildMouseUpInteraction(this.videoElement, mouseEvent));
        });
        this.bind('mouseleave', (mouseEvent) => {
            // When the mouse leaves the frame, we just end the mouse interaction by sending a MouseUp
            this.dataChannel.send(buildMouseUpInteraction(this.videoElement, mouseEvent));
        });
        this.bind('contextmenu', (mouseEvent) => {
            // When the context menu is shown then it is safest to release the button
            // which was pressed when the event happened. This will guarantee we will
            // get at least one mouse up corresponding to a mouse down event.
            this.dataChannel.send(buildMouseUpInteraction(this.videoElement, mouseEvent));
        });
    }
    registerTouchEvents() {
        // Since the AVE does not yet understand touch commands, we send mouse commands even on mobile
        this.logger.debug('Registering touch input events');
        this.bind('touchstart', (touchEvent) => {
            if (touchEvent.touches.length === 1) {
                this.dataChannel.send(buildMouseDownInteraction(this.videoElement, {
                    clientX: touchEvent.touches[0].clientX,
                    clientY: touchEvent.touches[0].clientY,
                    button: MouseButton.Left
                }));
            }
            else if (touchEvent.touches.length === 2) {
                this.previousPinchDistance = this.calculatePinchDistance(touchEvent.touches);
            }
            this.updatePreviousPosition(touchEvent.touches[0]);
            touchEvent.stopPropagation();
        });
        this.bind('touchend', (touchEvent) => {
            // use touchEvent.changedTouches instead of touchEvent.touches for touchend
            if (touchEvent.changedTouches.length === 1) {
                this.dataChannel.send(buildMouseUpInteraction(this.videoElement, {
                    clientX: touchEvent.changedTouches[0].clientX,
                    clientY: touchEvent.changedTouches[0].clientY,
                    button: MouseButton.Left
                }));
            }
            touchEvent.stopPropagation();
        });
        this.bind('touchmove', (touchEvent) => {
            if (touchEvent.touches.length === 1) {
                const touch = touchEvent.touches[0];
                this.dataChannel.send(buildMouseMoveInteraction(this.videoElement, {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    movementX: touch.clientX - this.previousPosition.clientX,
                    movementY: touch.clientY - this.previousPosition.clientY
                }));
                this.updatePreviousPosition(touch);
            }
            if (touchEvent.touches.length === 2) {
                const currentPinchDistance = this.calculatePinchDistance(touchEvent.touches);
                const pinchDistanceDifference = this.previousPinchDistance - currentPinchDistance;
                if (Math.abs(pinchDistanceDifference) >= inputConfig.pinchZoomThresholdInPixel) {
                    this.dataChannel.send(buildMouseWheelInteraction(this.videoElement, {
                        clientX: touchEvent.touches[0].clientX,
                        clientY: touchEvent.touches[0].clientY,
                        deltaY: pinchDistanceDifference
                    }));
                }
            }
            touchEvent.stopPropagation();
        });
    }
    updatePreviousPosition(event) {
        this.previousPosition = { clientX: event.clientX, clientY: event.clientY };
    }
    calculatePinchDistance(touches) {
        const distanceX = touches[0].clientX - touches[1].clientX;
        const distanceY = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(distanceX ** 2 + distanceY ** 2);
    }
}
//# sourceMappingURL=input-handler.js.map